import type { ProductionWorkflowState } from "@/lib/sdtestapp/sdTestAppProductionClient";

const STORAGE_KEY = "sdtestapp_hub_production_workflow_state";
const PAYLOAD_VERSION = 1;

export type HubProductionWorkflowPersisted = {
  version: typeof PAYLOAD_VERSION;
  port: string;
  stepIndex: number;
  maxStepIndexReached: number;
  savedAt: string;
};

export function readHubProductionWorkflowPersisted(port: string): HubProductionWorkflowPersisted | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw?.trim()) return null;
    const parsed = JSON.parse(raw) as Partial<HubProductionWorkflowPersisted>;
    if (Number(parsed.version) !== PAYLOAD_VERSION) return null;
    if (String(parsed.port ?? "").trim() !== port.trim()) return null;
    const stepIndex = Number(parsed.stepIndex);
    const maxStepIndexReached = Number(parsed.maxStepIndexReached);
    if (!Number.isFinite(stepIndex) || stepIndex < 0 || stepIndex > 10) return null;
    if (!Number.isFinite(maxStepIndexReached) || maxStepIndexReached < stepIndex) return null;
    return {
      version: PAYLOAD_VERSION,
      port: port.trim(),
      stepIndex,
      maxStepIndexReached,
      savedAt: String(parsed.savedAt ?? ""),
    };
  } catch {
    return null;
  }
}

export function writeHubProductionWorkflowPersisted(port: string, workflow: ProductionWorkflowState): void {
  if ((workflow.stepIndex ?? 0) <= 0) {
    clearHubProductionWorkflowPersisted();
    return;
  }
  const payload: HubProductionWorkflowPersisted = {
    version: PAYLOAD_VERSION,
    port: port.trim(),
    stepIndex: workflow.stepIndex ?? 0,
    maxStepIndexReached: workflow.maxStepIndexReached ?? workflow.stepIndex ?? 0,
    savedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function clearHubProductionWorkflowPersisted(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

export function restoredHubProductionStepIndex(
  engineWorkflow: ProductionWorkflowState,
  persisted: HubProductionWorkflowPersisted | null,
): number | null {
  if (!persisted) return null;
  const engineStep = engineWorkflow.stepIndex ?? 0;
  if (persisted.stepIndex <= 0) return null;
  if (engineStep >= persisted.stepIndex) return null;
  return persisted.stepIndex;
}
